import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  MultiSeriesBarChart,
  type MultiSeriesBarSeries,
} from '@/components/common/charts/MultiSeriesBarChart';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Card } from '@/components/ui/card';
import { buildTherapyTypeTranslationKey } from '@/constants/appointmentOptions';
import { useVisitsByTypeQuery } from '@/hooks/useReportsQueries';
import { resolveSeriesColor } from '@/theme/chartPalette';
import { formatDateToMonthAndYear } from '@/utils/dateFormatting';
import type { ReportsDateRangeValue } from './types';
import { toDateRangeParameters } from './types';

interface VisitsByTypeChartProps {
  dateRange: ReportsDateRangeValue;
}

export function VisitsByTypeChart({ dateRange }: VisitsByTypeChartProps) {
  const { t } = useTranslation(['reports', 'appointments']);
  const query = useVisitsByTypeQuery(toDateRangeParameters(dateRange));
  const rows = query.data === undefined ? [] : query.data;

  const periods = Array.from(
    new Set(
      rows.map(function toPeriod(row) {
        return row.period;
      })
    )
  ).sort();
  const therapyTypes = Array.from(
    new Set(
      rows.map(function toTherapyType(row) {
        return row.therapyType;
      })
    )
  );

  const data: Array<Record<string, string | number>> = periods.map(function toRow(period) {
    const row: Record<string, string | number> = { period };
    for (const therapyType of therapyTypes) {
      row[therapyType] = 0;
    }
    for (const visit of rows) {
      if (visit.period === period) {
        row[visit.therapyType] = visit.count;
      }
    }
    return row;
  });
  const series: MultiSeriesBarSeries[] = therapyTypes.map(function toSeries(therapyType, index) {
    return {
      key: therapyType,
      label: t(buildTherapyTypeTranslationKey(therapyType)),
      color: resolveSeriesColor(index),
    };
  });

  return (
    <Card>
      <h2 className="text-sm font-bold text-zooindigo mb-3">
        {t('reports:charts.visitsByTypeTitle')}
      </h2>
      {query.isLoading === true ? (
        <div className="py-10 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : query.isError === true ? (
        <QueryErrorState
          onRetry={function retry() {
            query.refetch();
          }}
        />
      ) : (
        <MultiSeriesBarChart
          data={data}
          xKey="period"
          series={series}
          accessibleLabel={t('reports:charts.visitsByTypeTitle')}
          emptyLabel={t('reports:charts.emptyState')}
          formatXTick={formatPeriodLabel}
          formatValue={formatWholeNumber}
        />
      )}
    </Card>
  );
}

function formatPeriodLabel(period: string): string {
  return formatDateToMonthAndYear(parseISO(period));
}

function formatWholeNumber(value: number): string {
  return String(value);
}
